// A React web app that generates and tracks running routes with OpenRouteService.
// It allows users to record their actual running path or generate recommended routes, name the route, calculate duration and average speed, save the run, view past history, and delete or compare runs. Includes elevation profile display.

import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Polyline } from 'react-leaflet';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import 'leaflet/dist/leaflet.css';

export default function RunningRouteApp() {
  const [tab, setTab] = useState('tracking');
  const [position, setPosition] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [recordedRoute, setRecordedRoute] = useState([]);
  const [recordStartTime, setRecordStartTime] = useState(null);
  const [history, setHistory] = useState([]);
  const [routeName, setRouteName] = useState("");
  const [recommendRoute, setRecommendRoute] = useState([]);
  const [distance, setDistance] = useState(3);
  const [routeType, setRouteType] = useState("loop");
  const [elevations, setElevations] = useState([]);
  const [currentPosition, setCurrentPosition] = useState(null);
  const watchIdRef = useRef(null);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = [pos.coords.latitude, pos.coords.longitude];
        setPosition(coords);
        setCurrentPosition(coords);
      },
      (err) => {
        console.error(err);
        alert("위치 정보를 가져올 수 없습니다.");
      }
    );

    const stored = localStorage.getItem("runHistory");
    if (stored) {
      setHistory(JSON.parse(stored));
    }
  }, []);

  const startTracking = () => {
    if (!position) return;
    setRecordedRoute([position]);
    setRecordStartTime(Date.now());
    setTracking(true);

    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const coords = [pos.coords.latitude, pos.coords.longitude];
        setCurrentPosition(coords);

        if (pos.coords.accuracy > 20) return; // 정확도 필터

        if (recordedRoute.length > 0) {
          const last = recordedRoute[recordedRoute.length - 1];
          const dist = calculateDistance([last, coords]);
          if (dist < 0.005) return; // 5m 이하 이동 무시
        }

        setRecordedRoute((prev) => [...prev, coords]);
      },
      (err) => console.error(err),
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 5000 }
    );
    watchIdRef.current = id;
  };

  const stopTracking = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    setTracking(false);

    const elapsed = Math.round((Date.now() - recordStartTime) / 1000);
    const distanceKm = calculateDistance(recordedRoute);
    const avgSpeed = (distanceKm / (elapsed / 3600)).toFixed(2);

    const run = {
      id: Date.now(),
      name: routeName || `기록 ${new Date().toLocaleString()}`,
      route: recordedRoute,
      time: elapsed,
      distance: distanceKm.toFixed(2),
      avgSpeed,
      date: new Date().toLocaleString()
    };

    const updatedHistory = [run, ...history];
    setHistory(updatedHistory);
    localStorage.setItem("runHistory", JSON.stringify(updatedHistory));
    alert(`저장 완료! 🛣 ${run.distance} km | ⏱ ${Math.floor(run.time / 60)}분 ${run.time % 60}초 | 🚀 ${avgSpeed} km/h`);
    setRouteName("");
    setRecordedRoute([]);
  };

  const calculateDistance = (coords) => {
    if (coords.length < 2) return 0;
    const toRad = (v) => (v * Math.PI) / 180;
    let total = 0;
    for (let i = 1; i < coords.length; i++) {
      const [lat1, lon1] = coords[i - 1];
      const [lat2, lon2] = coords[i];
      const R = 6371;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      total += R * c;
    }
    return total;
  };

  const deleteRun = (id) => {
    const updated = history.filter((run) => run.id !== id);
    setHistory(updated);
    localStorage.setItem("runHistory", JSON.stringify(updated));
  };


  const generateRoute = async () => {
    if (!position) return;
    const start = [position[1], position[0]];
    let trialOffset = 0.0025 * distance;
    let coords;

    if (routeType === "loop") {
      const p1 = [start[0] + trialOffset, start[1]];
      const p2 = [start[0] + trialOffset, start[1] + trialOffset];
      const p3 = [start[0], start[1] + trialOffset];
      coords = [start, p1, p2, p3, start];
    } else {
      coords = [start, [start[0], start[1] + trialOffset]];
    }

    const response = await fetch("https://api.openrouteservice.org/v2/directions/foot-walking/geojson", {
      method: "POST",
      headers: {
        'Authorization': '5b3ce3597851110001cf624804334ef53ab24d0687e6daafaec1f30e',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        coordinates: coords,
        preference: "recommended",
        options: { avoid_features: ["ferries", "fords", "steps"] }
      })
    });

    const data = await response.json();
    if (!data.features) {
      alert("경로 생성 실패");
      return;
    }

    const rawCoords = data.features[0].geometry.coordinates;
    const mapped = rawCoords.map(([lng, lat]) => [lat, lng]);
    setRecommendRoute(mapped);

    // Elevation fetch
    const elevationResponse = await fetch("https://api.openrouteservice.org/elevation/line", {
      method: "POST",
      headers: {
        'Authorization': '5b3ce3597851110001cf624804334ef53ab24d0687e6daafaec1f30e',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        format_in: "geojson",
        format_out: "geojson",
        geometry: {
          type: "LineString",
          coordinates: rawCoords
        }
      })
    });

    const elevationData = await elevationResponse.json();
    if (!elevationData.geometry || !elevationData.geometry.coordinates) {
      console.error("Elevation 응답 오류:", elevationData);
      alert("고도 데이터를 가져올 수 없습니다.");
      return;
    }

    const elevationList = elevationData.geometry.coordinates.map((c, i) => ({
      index: i,
      elevation: c[2]
    }));
    setElevations(elevationList);
  };

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold mb-4">러닝 루트 앱</h1>

      <div className="mb-4 flex gap-2">
        <button className={`px-4 py-1 rounded ${tab === 'tracking' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`} onClick={() => setTab('tracking')}>내 기록</button>
        <button className={`px-4 py-1 rounded ${tab === 'recommend' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`} onClick={() => setTab('recommend')}>추천 루트</button>
      </div>

      {tab === 'tracking' && (
        <>
          <div className="mb-4 flex gap-2 items-center">
            <input type="text" placeholder="경로 이름" value={routeName} onChange={(e) => setRouteName(e.target.value)} className="border px-2 py-1 rounded" />
            <button onClick={tracking ? stopTracking : startTracking} className={`px-4 py-2 rounded text-white ${tracking ? 'bg-red-500' : 'bg-green-600'}`}>{tracking ? "종료" : "시작"}</button>
          </div>

          {position && (
            <MapContainer center={position} zoom={15} style={{ height: "400px", width: "100%" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
              <Marker position={position} />
              {recordedRoute.length > 0 && (
                <>
                  <Polyline positions={recordedRoute} color="blue" />
                  <Marker position={recordedRoute[0]} /> {/* 시작점 */}
                  <Marker position={recordedRoute[recordedRoute.length - 1]} /> {/* 종료점 */}
                </>
              )}
              {currentPosition && tracking && (
                <Marker position={currentPosition} />
              )}
              {history.map(run => (
                <Polyline key={run.id} positions={run.route} color="purple" />
              ))}
            </MapContainer>
          )}

          <div className="mt-6">
            <h2 className="text-lg font-semibold mb-2">기록된 러닝</h2>
            {history.length === 0 && <p className="text-gray-500">아직 저장된 기록이 없습니다.</p>}
            <ul className="space-y-2">
              {history.map((run) => (
                <li key={run.id} className="border p-2 rounded flex flex-col md:flex-row justify-between md:items-center">
                  <span>
                    🏷️ {run.name} | 🗓 {run.date} | 🛣 {run.distance}km | ⏱ {Math.floor(run.time / 60)}:{run.time % 60} | 🚀 {run.avgSpeed} km/h
                  </span>
                  <button onClick={() => deleteRun(run.id)} className="text-red-500 underline mt-2 md:mt-0">삭제</button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {tab === 'recommend' && (
        <div>
          <div className="flex gap-4 mb-4">
            <label>루트 거리 (km):</label>
            <select value={distance} onChange={(e) => setDistance(Number(e.target.value))} className="border p-1">
              {[1, 2, 3, 5, 7, 10].map(d => (<option key={d} value={d}>{d}km</option>))}
            </select>
            <label>유형:</label>
            <select value={routeType} onChange={(e) => setRouteType(e.target.value)} className="border p-1">
              <option value="loop">순환</option>
              <option value="oneway">편도</option>
            </select>
            <button onClick={generateRoute} className="bg-blue-500 text-white px-3 py-1 rounded">루트 생성</button>
          </div>

          {position && (
            <MapContainer center={position} zoom={15} style={{ height: "400px", width: "100%" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
              <Marker position={position} />
              {recommendRoute.length > 0 && (<Polyline positions={recommendRoute} color="red" />)}
            </MapContainer>
          )}

          {elevations.length > 0 && (
            <div className="mt-6">
              <h2 className="text-md font-semibold mb-2">고도 프로파일</h2>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={elevations} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="index" tick={false} />
                  <YAxis unit="m" />
                  <Tooltip />
                  <Line type="monotone" dataKey="elevation" stroke="#8884d8" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
